import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Slider,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDButton from "components/MDButton";
import GhostMosaicOverlay from "./components/GhostMosaicOverlay";
import TileCellGridOverlay from "./components/TileCellGridOverlay";

export default function CaptureGridDialog({
  open,
  onClose,
  isMobile,
  videoRef,
  captureViewportRef,
  fallbackCameraInputRef,
  handleFallbackCaptureFile,

  // Grid + progress
  gridRows,
  gridCols,
  setGridRows,
  setGridCols,
  totalTiles,
  capturedCount,
  capturePercent,

  // Current tile
  currentTile,
  currentTileKey,
  currentRow,
  currentCol,
  capturedTiles,
  onSelectTile,

  // Capture control
  captureFeedback,
  needsRetake,
  captureBusy,
  cameraReady,
  captureCurrentTile,
  removeCurrentTile,
  startStitchPipeline,
  stitchJobId,
  stitchBusy,
  stitchStatus,
  stitchPipelinePercent,
  stitchPipelinePhase,
  stitchPipelineMessage,

  // Ghost overlay + overlap feedback
  ghostEnabled,
  ghostOpacity,
  setGhostEnabled,
  setGhostOpacity,
  overlapScore,
  overlapHint,
  referenceStripUrl,
  liveStripUrl,
}) {
  const classifyOverlap = (score) => {
    if (score == null) return { label: "", severity: "info" };
    if (score >= 75) return { label: "Good alignment", severity: "success" };
    if (score >= 55) return { label: "Acceptable, refine a bit", severity: "warning" };
    return { label: "Risk of gap/shift, realign before capture", severity: "error" };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={{ pr: 6 }}>
        Capture Slide
        <span
          style={{
            marginLeft: 10,
            padding: "2px 10px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            background: "rgba(244, 67, 54, 0.18)",
            color: "#f44336",
            verticalAlign: "middle",
          }}
        >
          Bita
        </span>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1.5, flexWrap: "wrap" }}>
          <Typography variant="caption">Rows</Typography>
          <Slider
            value={gridRows}
            onChange={(_, v) => setGridRows(Number(v))}
            min={2}
            max={10}
            step={1}
            valueLabelDisplay="auto"
            sx={{ width: 120 }}
          />
          <Typography variant="caption">Cols</Typography>
          <Slider
            value={gridCols}
            onChange={(_, v) => setGridCols(Number(v))}
            min={2}
            max={10}
            step={1}
            valueLabelDisplay="auto"
            sx={{ width: 120 }}
          />
          <Typography variant="body2" sx={{ ml: "auto" }}>
            {capturedCount}/{totalTiles} ({capturePercent}%)
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={capturePercent}
          sx={{ height: 8, borderRadius: 999, mb: 1.5 }}
        />

        <Box
              ref={captureViewportRef}
          sx={{
            position: "relative",
            width: "100%",
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.18)",
            bgcolor: "#000",
            aspectRatio: "4/3",
            "@keyframes tilePulse": {
              "0%": { boxShadow: "0 0 0 0 rgba(0,229,255,0.8)" },
              "70%": { boxShadow: "0 0 0 8px rgba(0,229,255,0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(0,229,255,0)" },
            },
          }}
        >
          <video
            ref={videoRef}
            muted
            playsInline
            autoPlay
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />

          <GhostMosaicOverlay
            ghostEnabled={ghostEnabled}
            capturedCount={capturedCount}
            ghostOpacity={ghostOpacity}
            capturedTiles={capturedTiles}
            gridCols={gridCols}
            gridRows={gridRows}
            totalTiles={totalTiles}
          />

          <TileCellGridOverlay
            capturedTiles={capturedTiles}
            gridCols={gridCols}
            gridRows={gridRows}
            totalTiles={totalTiles}
            currentTile={currentTile}
            onSelectTile={onSelectTile}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1.2,
            alignItems: "center",
            mt: 1,
            mb: 1,
            flexWrap: "wrap",
          }}
        >
          <MDButton
            color={ghostEnabled ? "info" : "secondary"}
            variant={ghostEnabled ? "contained" : "outlined"}
            onClick={() => setGhostEnabled((v) => !v)}
            sx={{ borderRadius: 999, minWidth: 130 }}
            disabled={capturedCount === 0}
          >
            {ghostEnabled ? "Ghost On" : "Ghost Off"}
          </MDButton>
          <Typography variant="caption">Ghost opacity</Typography>
          <Slider
            value={ghostOpacity}
            onChange={(_, v) => setGhostOpacity(Number(v))}
            min={0}
            max={100}
            step={5}
            valueLabelDisplay="auto"
            sx={{ width: 140 }}
            disabled={!ghostEnabled || capturedCount === 0}
          />
          {overlapScore != null ? (
            <Alert
              severity={classifyOverlap(overlapScore).severity}
              sx={{ py: 0, px: 1, borderRadius: 999, ml: "auto" }}
            >
              {overlapHint} ({overlapScore}%)
            </Alert>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
              Capture first neighbor tile to start alignment scoring.
            </Typography>
          )}
        </Box>

        {referenceStripUrl && liveStripUrl ? (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
            <Box sx={{ border: "1px solid #ddd", borderRadius: 1, overflow: "hidden" }}>
              <Typography
                variant="caption"
                sx={{ px: 1, py: 0.3, display: "block", bgcolor: "#f5f5f5" }}
              >
                Reference edge
              </Typography>
              <img
                src={referenceStripUrl}
                alt="Reference edge"
                style={{ width: "100%", maxHeight: 80, objectFit: "cover", display: "block" }}
              />
            </Box>
            <Box sx={{ border: "1px solid #ddd", borderRadius: 1, overflow: "hidden" }}>
              <Typography
                variant="caption"
                sx={{ px: 1, py: 0.3, display: "block", bgcolor: "#f5f5f5" }}
              >
                Live edge
              </Typography>
              <img
                src={liveStripUrl}
                alt="Live edge"
                style={{ width: "100%", maxHeight: 80, objectFit: "cover", display: "block" }}
              />
            </Box>
          </Box>
        ) : null}

        {captureFeedback ? (
          <Typography
            variant="body2"
            sx={{ mt: 1.2, color: needsRetake ? "warning.main" : "success.main", fontWeight: 600 }}
          >
            {captureFeedback}
          </Typography>
        ) : null}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.6, display: "block" }}>
          Tile {currentRow + 1},{currentCol + 1} —{" "}
          {capturedTiles?.[currentTileKey]?.status === "uploading"
            ? `Uploading ${capturedTiles?.[currentTileKey]?.progress || 0}%`
            : capturedTiles?.[currentTileKey]?.status === "error"
              ? capturedTiles?.[currentTileKey]?.error || "Upload failed"
              : capturedTiles?.[currentTileKey]?.done
                ? "Uploaded"
                : "Pending"}
        </Typography>

        {stitchJobId ? (
          <Box sx={{ mt: 0.8 }}>
            <Typography variant="caption" color="info.main" sx={{ display: "block", mb: 0.4 }}>
              Stitch job: {stitchJobId} {stitchStatus ? `(${stitchStatus})` : ""}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={stitchPipelinePercent}
              sx={{ height: 8, borderRadius: 999 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.35, display: "block" }}>
              {stitchPipelinePhase ? `${stitchPipelinePhase.toUpperCase()} — ` : ""}
              {stitchPipelineMessage || "Processing..."} ({stitchPipelinePercent}%)
            </Typography>
          </Box>
        ) : null}

        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, flexWrap: "wrap", gap: 1 }}>
          <input
            ref={fallbackCameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={handleFallbackCaptureFile}
          />
          <MDButton
            color={needsRetake ? "warning" : "info"}
            variant="contained"
            disabled={!cameraReady || captureBusy}
            onClick={captureCurrentTile}
            sx={{
              minWidth: 170,
              minHeight: 56,
              borderRadius: 999,
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            {captureBusy ? (
              <CircularProgress size={22} color="inherit" />
            ) : needsRetake ? (
              "Retake"
            ) : (
              "Capture"
            )}
          </MDButton>
          <MDButton
            color="error"
            variant="outlined"
            disabled={captureBusy || !capturedTiles?.[currentTileKey]?.done}
            onClick={removeCurrentTile}
            sx={{
              minWidth: 150,
              minHeight: 56,
              borderRadius: 999,
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            Delete Tile
          </MDButton>
          {capturedCount === totalTiles ? (
            <MDButton
              color="success"
              variant="contained"
              disabled={captureBusy || stitchBusy || !!stitchJobId}
              onClick={startStitchPipeline}
              sx={{
                minWidth: 190,
                minHeight: 56,
                borderRadius: 999,
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              {stitchBusy || stitchStatus === "processing" ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Compress & Tile"
              )}
            </MDButton>
          ) : null}
          {!window.isSecureContext ? (
            <MDButton
              color="info"
              variant="outlined"
              disabled={captureBusy}
              onClick={() => fallbackCameraInputRef.current?.click()}
              sx={{
                ml: 1.2,
                minWidth: 170,
                minHeight: 56,
                borderRadius: 999,
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              Open Camera
            </MDButton>
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

